import { inject, injectable, injectWithTransform } from "tsyringe";
import { Geo, Logger, LoggerTransformer, waitFor } from "@identity/utils";
import { ConsentProviderInterface } from "../consent-provider-interface";
import { ConsentResolvedCallback } from "../../types";
import { WINDOW } from "@identity/symbols";
import { KetchTracker } from "./ketch-tracker";

export enum KetchCommands {
  on = "on",
  once = "once",
  off = "off",
}

export enum KetchEvents {
  consent = "consent",
  userConsentUpdated = "userConsentUpdated",
  willShowExperience = "willShowExperience",
  hideExperience = "hideExperience",
  regionInfo = "regionInfo",
  geoip = "geoip",
  jurisdiction = "jurisdiction",
  environment = "environment",
  identities = "identities",
}

export interface KetchSetup {
  purposes: {
    tracking: boolean;
    behavioral_advertising: boolean;
    essential_services: boolean;
    analytics: boolean;
  };
  vendors: string[];
}

@injectable()
export class Ketch implements ConsentProviderInterface {
  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "Ketch")
    private readonly logger: Logger,
    @inject(WINDOW) private readonly window: Window,
    @inject(Geo) private readonly geo: Geo,
    @inject(KetchTracker) private readonly ketchTracker: KetchTracker,
  ) {
    this.logger.info("Ketch initialized");
    this.ketchTracker.init();
  }

  userInteractedWithModal() {
    return false;
  }

  addKetchListener(callback: ConsentResolvedCallback): void {
    waitFor(() => !!this.window.ketch).then(() => {
      this.addTrackers();
      this.window.ketch(
        KetchCommands.on,
        KetchEvents.consent,
        (res: KetchSetup) => {
          if (res?.purposes && Object.keys(res.purposes).length > 1) {
            this.logger.info("Ketch consent event received", res);
            callback({
              ccpaSignal:
                !res.purposes.tracking && !res.purposes.behavioral_advertising,
              geoRequiresSignal: this.geo.getCountryCode() === "us",
              gdprConsent:
                res.purposes.tracking && res.purposes.behavioral_advertising,
              geoRequiresConsent: this.geo.isGdprCountry(),
            });
          } else {
            this.logger.info("Ketch consent is not ready", res);
          }
        },
      );
    });
  }

  addTrackers() {
    if (document.getElementById("ketch-banner")) {
      this.ketchTracker.sendImpressionEvent();
    } else {
      this.logger.info("Ketch banner not found");
    }
    this.window.ketch(KetchCommands.on, KetchEvents.willShowExperience, () => {
      this.window.ketch(KetchCommands.once, KetchEvents.consent, () => {
        this.ketchTracker.sendImpressionEvent();
      });
    });

    this.window.ketch(
      KetchCommands.on,
      KetchEvents.userConsentUpdated,
      (res: KetchSetup) => {
        this.ketchTracker.sendActionEvent(
          res.purposes.tracking && res.purposes.behavioral_advertising
            ? "accept"
            : "reject",
        );
      },
    );

    this.logger.info("Trackers added");
  }

  subscribe(callback: ConsentResolvedCallback): void {
    this.logger.info("Subscribed");

    this.addKetchListener(callback);
  }
}
